import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import type { LbStatus } from "../lib/lbApi";

export type LbMonitorLine = { ts: string; line: string; sp?: string };

const STREAM_MAX = 200;
const EVENTS_MAX = 100;
const SENT_MAX = 1000;

function asLines(raw: unknown, max: number): LbMonitorLine[] {
  if (!Array.isArray(raw)) return [];
  const out: LbMonitorLine[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as { ts?: unknown; line?: unknown; sp?: unknown };
    out.push({ ts: String(row.ts || ""), line: String(row.line || ""), sp: String(row.sp || "") });
  }
  return out.slice(-max);
}

function appendLine(prev: LbMonitorLine[], item: LbMonitorLine, max: number) {
  return [...prev, item].slice(-max);
}

export function useLbMonitor(streamOn: boolean) {
  const location = useLocation();
  const enabled = location.pathname === "/fcr";
  const [status, setStatus] = useState<LbStatus | null>(null);
  const [stream, setStream] = useState<LbMonitorLine[]>([]);
  const [events, setEvents] = useState<LbMonitorLine[]>([]);
  const [sent, setSent] = useState<LbMonitorLine[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }
    const es = new EventSource(`/api/lb/monitor?stream=${streamOn ? "1" : "0"}`);
    setError(null);
    es.addEventListener("snapshot", (ev) => {
      try {
        const body = JSON.parse((ev as MessageEvent).data) as {
          status?: LbStatus | null;
          stream?: unknown;
          sent?: unknown;
          events?: unknown;
        };
        setStatus(body.status ?? null);
        if (streamOn) {
          const lines = asLines(body.stream, STREAM_MAX);
          if (lines.length) setStream(lines);
          const sentLines = asLines(body.sent, SENT_MAX);
          if (sentLines.length) setSent(sentLines);
        }
        setEvents(asLines(body.events, EVENTS_MAX));
        setConnected(true);
        setError(null);
      } catch {
        setError("invalid load-balancing snapshot");
      }
    });
    es.addEventListener("status", (ev) => {
      try {
        setStatus(JSON.parse((ev as MessageEvent).data) as LbStatus);
      } catch {
        /* ignore */
      }
    });
    es.addEventListener("stream", (ev) => {
      try {
        const item = JSON.parse((ev as MessageEvent).data) as LbMonitorLine;
        if (!item?.line) return;
        setStream((prev) => appendLine(prev, item, STREAM_MAX));
      } catch {
        /* ignore */
      }
    });
    es.addEventListener("sent", (ev) => {
      try {
        const body = JSON.parse((ev as MessageEvent).data) as { items?: unknown };
        const items = asLines(body.items, SENT_MAX);
        if (items.length) setSent((prev) => [...prev, ...items].slice(-SENT_MAX));
      } catch {
        /* ignore */
      }
    });
    es.addEventListener("event", (ev) => {
      try {
        const item = JSON.parse((ev as MessageEvent).data) as LbMonitorLine;
        if (!item?.line) return;
        setEvents((prev) => appendLine(prev, item, EVENTS_MAX));
      } catch {
        /* ignore */
      }
    });
    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        setConnected(false);
        setError((prev) => prev ?? "load-balancing stream closed");
      }
    };
    return () => {
      es.close();
      setConnected(false);
    };
  }, [enabled, location.key, streamOn]);

  return { status, stream, sent, events, connected, error };
}
